import { Form, Heading, HeadingLevel } from "@ariakit/react";
import {
  DownloadLink,
  LoadingView,
  SubmitErrorAlert,
} from "@indietabletop/appkit";
import { fmt } from "../App/fmt";
import { href } from "../App/href";
import { CartProvider } from "../cart/cart";
import { useInvokeClient, type Product } from "../client";
import { fragment } from "../common.css";
import { FailureView } from "../FailureView/FailureView";
import { Page, ViewContent } from "../oss/Layout/Layout";
import { CheckoutButton } from "../ShopPage/CheckoutButton";
import { ProductCard } from "../ShopPage/ProductCard";
import { ShoppingCartPill } from "../ShopPage/ShoppingCart";
import { useCheckoutForm } from "../ShopPage/useCheckoutForm";
import { useScrollToFragment } from "../ShopPage/useScrollToFragment";
import gravehelm from "./gravehelmLogo.svg";
import { gravehelmTheme, main, product, productsIntro } from "./style.css";

const stls = "stls";
const gravehelmDownload =
  "https://indietabletop.s3.eu-central-1.amazonaws.com/freebies/malev/Gravehelm+2026-10-30.pdf";

function MainSection(props: { products: Product[] }) {
  const { products } = props;

  const unownedProducts = products.filter((p) => !p.ownership);
  const { form, submitName } = useCheckoutForm({
    products: unownedProducts,
    cancelTo: href("~/gravehelm"),
  });

  return (
    <div className={main.container}>
      <header className={main.header}>
        <h1>
          <img src={gravehelm} alt="Gravehelm" className={main.headingImage} />
        </h1>
        <p className={main.lead}>
          A free spooky scenario and lore pack for Greathelm.
        </p>

        <div className={main.actions}>
          <DownloadLink href={gravehelmDownload} className={main.download}>
            Download Packet
          </DownloadLink>
        </div>
      </header>

      <div className={main.content}>
        <p className={main.paragraph}>
          This spooky Halloween packet includes a challenging scenario featuring
          2 scenes where brave adventurers face off against skeletal and armored
          horrors risen from the earth! This packet features new rules,
          including those that introduce solo and cooperative play to the
          quickstart format so you can take on undead evils by yourself or with
          your loved ones.
        </p>

        <p className={main.paragraph}>
          Missing some bits to play in style? We've put together everything you
          might need to get Gravehelm on the table! From lightly armoured
          soldiers (perfect as the scenario's adventurers) to a modular skeleton
          set by Finsterfey Miniatures to represent the undead menace!
        </p>
      </div>

      <div>
        <div className={main.products}>
          {products.map((p) => {
            return (
              <a key={p.code} href={`#${p.code}`} className={product.container}>
                {p.thumbnailSrc && (
                  <img src={p.thumbnailSrc} alt="" className={product.image} />
                )}

                <div className={product.content}>
                  <div className={product.name}>{p.name}</div>
                  <div className={product.info}>
                    {p.ownership ? "Owned" : fmt.currency(p.currency, p.price)}
                  </div>
                </div>
              </a>
            );
          })}
        </div>

        <div className={main.purchaseActions}>
          {unownedProducts.length > 0 && (
            <Form store={form} className={fragment}>
              <CheckoutButton
                products={unownedProducts}
                className={main.buy}
                backToIfAccountNeeded={href("~/gravehelm")}
                redirectToIfAccountNeeded={href("~/gravehelm", {
                  fragment: stls,
                })}
              >
                {"Buy All "}
                {fmt.currency(
                  products.at(0)?.currency ?? "usd",
                  products.reduce((sum, p) => sum + p.price, 0),
                )}
              </CheckoutButton>
              <SubmitErrorAlert name={submitName} />
            </Form>
          )}

          <a href="#stls" className={main.browse}>
            Browse STLs
          </a>
        </div>
      </div>
    </div>
  );
}

function GravehelmPageSuccess(props: {
  products: Product[];
  title: string;
  backHref: string;
}) {
  useScrollToFragment();

  const gravehelmProducts = props.products.filter((product) =>
    product.name.startsWith("Gravehelm"),
  );

  return (
    <CartProvider products={gravehelmProducts}>
      <Page {...props}>
        <ViewContent>
          <MainSection products={gravehelmProducts} />

          <HeadingLevel>
            <div className={productsIntro.header}>
              <Heading id={stls} className={productsIntro.heading}>
                Limited Edition Gravehelm STLs
              </Heading>
              <p className={productsIntro.info}>Available until November 30</p>
            </div>

            {gravehelmProducts.map((product) => {
              return <ProductCard key={product.id} product={product} />;
            })}
          </HeadingLevel>
        </ViewContent>

        <ShoppingCartPill />
      </Page>
    </CartProvider>
  );
}

export function GravehelmPage() {
  const [result] = useInvokeClient("getShopProducts", ["greathelm", "usd"]);

  const pageProps = {
    title: "Gravehelm",
    backHref: "~/",
    className: gravehelmTheme,
  };

  if (result.isPending) {
    return (
      <Page {...pageProps}>
        <LoadingView />
      </Page>
    );
  }

  if (result.isFailure) {
    return (
      <Page {...pageProps}>
        <FailureView failure={result.failure} />
      </Page>
    );
  }

  return <GravehelmPageSuccess {...pageProps} products={result.value.items} />;
}
